import { cx } from '@growup/ui';
import { useState } from 'react';

import {
  canApproveMembers,
  canManageMembers,
  canManageStreaming,
  canViewMembers,
  type StaffSession,
} from '../session';
import { AdultsView } from './AdultsView';
import { ApplicationsView } from './ApplicationsView';
import { AssociatesView } from './AssociatesView';
import { ImportsView } from './ImportsView';
import { MapView } from './MapView';
import { MembersView } from './MembersView';

type Tab = 'pedidos' | 'membros' | 'associados' | 'mapa' | 'importados' | 'adultos';

/**
 * Members section (RF-F1): applications, the member list, associates, the map,
 * imported records still to complete and youths who turned 18, by permission.
 * `openAccountId` lands on that member's record (from Streaming).
 */
export function MembersSection({
  session,
  openAccountId = null,
  onOpenStreamer,
}: {
  session: StaffSession;
  openAccountId?: string | null;
  onOpenStreamer?: ((accountId: string) => void) | undefined;
}) {
  const canApprove = canApproveMembers(session);
  const canView = canViewMembers(session);
  const [tab, setTab] = useState<Tab>(
    canApprove && !(canView && openAccountId !== null) ? 'pedidos' : 'membros',
  );
  const [selectedId, setSelectedId] = useState<string | null>(openAccountId);

  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'pedidos', label: 'Pedidos', show: canApprove },
    { id: 'membros', label: 'Membros', show: canView },
    { id: 'associados', label: 'Associados', show: canView },
    { id: 'mapa', label: 'Mapa', show: canView },
    { id: 'importados', label: 'Importados', show: canView },
    { id: 'adultos', label: 'Fizeram 18', show: canView },
  ];

  return (
    <div className="flex flex-col gap-4">
      <SubNav tabs={tabs.filter((t) => t.show)} current={tab} onChange={setTab} label="Sócios" />
      {tab === 'pedidos' && canApprove ? (
        <ApplicationsView
          onOpenMember={(id) => {
            if (!canView) return;
            setSelectedId(id);
            setTab('membros');
          }}
        />
      ) : null}
      {tab === 'membros' && canView ? (
        <MembersView
          canManage={canManageMembers(session)}
          canSeeStreamerNumbers={canManageStreaming(session)}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onOpenStreamer={canManageStreaming(session) ? onOpenStreamer : undefined}
        />
      ) : null}
      {tab === 'associados' && canView ? <AssociatesView /> : null}
      {tab === 'mapa' && canView ? <MapView /> : null}
      {tab === 'importados' && canView ? (
        <ImportsView canManage={canManageMembers(session)} />
      ) : null}
      {tab === 'adultos' && canView ? <AdultsView canManage={canManageMembers(session)} /> : null}
    </div>
  );
}

export function SubNav<T extends string>({
  tabs,
  current,
  onChange,
  label,
}: {
  tabs: { id: T; label: string }[];
  current: T;
  onChange: (tab: T) => void;
  label: string;
}) {
  if (tabs.length < 2) return null;
  return (
    // A linha de baixo e uma sombra interior, nao uma borda: com overflow-x a
    // margem -1px dos botoes criava scroll vertical. Assim os separadores
    // deslizam para o lado num telemovel sem alargar a pagina.
    <nav
      className="flex gap-1 overflow-x-auto shadow-[inset_0_-1px_0_rgb(var(--gu-line))] [scrollbar-width:none]"
      aria-label={label}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          aria-current={current === t.id ? 'page' : undefined}
          onClick={() => onChange(t.id)}
          className={cx(
            'shrink-0 whitespace-nowrap border-b-2 px-4 py-2 font-sub text-label font-bold uppercase',
            current === t.id
              ? 'border-gu-accent text-gu-ink'
              : 'border-transparent text-gu-ink-muted hover:text-gu-ink',
          )}
        >
          {t.label}
        </button>
      ))}
    </nav>
  );
}
